import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  IconButton,
  Switch,
  FormControlLabel,
  TextField,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tooltip,
} from '@mui/material';
import { 
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  ExpandMore as ExpandMoreIcon,
  Description as DescriptionIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { patchQuestionAnswer, type PatchQuestionAnswerBody } from '../utils/questionnaireApi';
import {
  cleanQuestionNumber,
  cleanQuestionText,
  formatRetrievalDistance,
  averageRetrievalDistanceLabel,
  sortRetrievalSourcesByDistance,
  RETRIEVAL_DISTANCE_TOOLTIP,
  AVERAGE_RETRIEVAL_DISTANCE_TOOLTIP,
  RETRIEVAL_SCORE_LEGEND,
} from '../utils/qaHelpers';
import { Answer } from '../hooks/useStreamingAnswers';
import { colors } from '../../../shared/styles/theme';
import { CopyAnswerWithOriginalMenu } from './CopyAnswerWithOriginalMenu';
import { QuestionEnglishInline } from './QuestionEnglishInline';

// Constants for textarea resize
const TEXTAREA_MIN_HEIGHT = 40;
const TEXTAREA_MAX_HEIGHT = 300;

// Helper function to resize textarea
const resizeTextarea = (textarea: HTMLTextAreaElement) => {
  textarea.style.height = 'auto';
  const scrollHeight = textarea.scrollHeight;
  textarea.style.height = `${Math.max(TEXTAREA_MIN_HEIGHT, Math.min(TEXTAREA_MAX_HEIGHT, scrollHeight))}px`;
};

/**
 * Skeleton component for loading state of an answer card.
 * Shows placeholder shapes while the actual answer is being loaded.
 */
export const AnswerCardSkeleton: React.FC = () => {
  return (
    <Box sx={{ mb: 4 }}>
      {/* Question skeleton with numbering */}
      <Typography variant="body1" sx={{ 
        mb: 2, 
        lineHeight: 1.5, 
        color: colors.primaryLight,
        fontSize: '1rem',
        fontWeight: 600
      }}>
        <Skeleton 
          variant="text" 
          width="85%" 
          height={24}
          sx={{ 
            display: 'inline-block',
            bgcolor: '#e0e0e0',
          }} 
        />
      </Typography>
      
      <Paper sx={{ 
        p: 3, 
        backgroundColor: 'white',
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        mb: 2,
        position: 'relative',
        pb: 5
      }}>
        {/* Answer text skeleton - multiple lines with varying lengths */}
        <Box sx={{ 
          mb: 2, 
          maxHeight: '150px',
          overflowY: 'auto',
          pr: 1
        }}>
          <Skeleton variant="text" width="100%" height={18} sx={{ mb: 1.5, bgcolor: '#f5f5f5' }} />
          <Skeleton variant="text" width="98%" height={18} sx={{ mb: 1.5, bgcolor: '#f5f5f5' }} />
          <Skeleton variant="text" width="95%" height={18} sx={{ mb: 1.5, bgcolor: '#f5f5f5' }} />
          <Skeleton variant="text" width="100%" height={18} sx={{ mb: 1.5, bgcolor: '#f5f5f5' }} />
          <Skeleton variant="text" width="92%" height={18} sx={{ mb: 1.5, bgcolor: '#f5f5f5' }} />
          <Skeleton variant="text" width="88%" height={18} sx={{ mb: 1.5, bgcolor: '#f5f5f5' }} />
          <Skeleton variant="text" width="75%" height={18} sx={{ bgcolor: '#f5f5f5' }} />
        </Box>
        
        {/* Action buttons area skeleton */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mt: 2,
          pt: 2,
          borderTop: '1px solid #e0e0e0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: '#f0f0f0', border: '1px solid #e0e0e0' }} />
            <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: '#f0f0f0', border: '1px solid #e0e0e0' }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
              <Skeleton variant="rectangular" width={40} height={20} sx={{ borderRadius: '10px', bgcolor: '#f0f0f0' }} />
              <Skeleton variant="text" width={70} height={20} sx={{ ml: 0.5, bgcolor: '#f0f0f0' }} />
            </Box>
          </Box>
          <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 0, bgcolor: '#f0f0f0', border: '1px solid #e0e0e0' }} />
        </Box>
      </Paper>
    </Box>
  );
};

interface AnswerCardProps {
  answer: Answer;
  onAnswerEdit?: (answerId: string, newAnswer: string) => void;
}

/**
 * Card component for displaying a single Q&A answer with editing and feedback capabilities.
 * Includes copy, like/dislike, edit mode, and feedback submission functionality.
 */
export const AnswerCard: React.FC<AnswerCardProps> = ({ answer, onAnswerEdit }) => {
  // Initialize feedback from backend if available
  const getInitialFeedback = (): 'liked' | 'disliked' | null => {
    if (answer.feedback_rating) {
      const rating = answer.feedback_rating.toUpperCase();
      if (rating === 'LIKED') return 'liked';
      if (rating === 'DISLIKED') return 'disliked';
    }
    return null;
  };

  // Initialize hasDislikedEdits if there are existing edits/feedback when disliked
  const getInitialHasDislikedEdits = (): boolean => {
    const feedback = getInitialFeedback();
    return feedback === 'disliked' && (!!answer.user_edited_ans || !!answer.feedback_reason);
  };

  const [isEditMode, setIsEditMode] = useState(false);
  const [feedback, setFeedback] = useState<'liked' | 'disliked' | null>(getInitialFeedback);
  const [editedAnswer, setEditedAnswer] = useState(answer.user_edited_ans || answer.answer);
  const [feedbackText, setFeedbackText] = useState(answer.feedback_reason || '');
  const [savedFeedbackText, setSavedFeedbackText] = useState(answer.feedback_reason || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [hasDislikedEdits, setHasDislikedEdits] = useState(getInitialHasDislikedEdits);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea based on content
  useEffect(() => {
    if (isEditMode && textareaRef.current) {
      resizeTextarea(textareaRef.current);
    }
  }, [editedAnswer, isEditMode]);

  // Notify parent of initial edited answer (if user_edited_ans exists from backend)
  useEffect(() => {
    if (onAnswerEdit && answer.user_edited_ans) {
      onAnswerEdit(answer.id, answer.user_edited_ans);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const updateQAF = async (updates: {
    feedback_rating?: string;
    feedback_reason?: string;
    user_edited_ans?: string;
  }) => {
    if (!answer.qaf_id) {
      setError('QAF ID not available. Cannot save changes.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const requestBody: PatchQuestionAnswerBody = {};

      if (updates.feedback_rating) {
        requestBody.feedback_rating = updates.feedback_rating;
      }
      if (updates.feedback_reason) {
        requestBody.feedback_reason = updates.feedback_reason;
      }
      if (updates.user_edited_ans !== undefined) {
        requestBody.user_edited_ans = updates.user_edited_ans;
      }

      if (Object.keys(requestBody).length === 0) {
        setError('At least one field must be provided.');
        setSaving(false);
        return;
      }

      await patchQuestionAnswer(answer.qaf_id, requestBody);

      setSuccessMessage('Changes saved successfully.');
      if (updates.user_edited_ans !== undefined) {
        setIsEditMode(false);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to save changes. Please try again.';
      setError(errorMessage);
      console.error('Error updating QAF:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleThumbUp = async () => {
    const newFeedback = feedback === 'liked' ? null : 'liked';
    
    // If switching from disliked to liked, show confirmation dialog
    if (feedback === 'disliked' && newFeedback === 'liked' && hasDislikedEdits) {
      setShowConfirmDialog(true);
      return;
    }
    
    setFeedback(newFeedback);
    if (newFeedback === 'liked') {
      if (isEditMode) {
        setIsEditMode(false);
        setEditedAnswer(answer.answer);
        if (onAnswerEdit) {
          onAnswerEdit(answer.id, answer.answer);
        }
      }
      setFeedbackText('');
      setSavedFeedbackText('');
      setHasDislikedEdits(false);
      await updateQAF({ feedback_rating: 'LIKED' });
    }
  };

  const handleConfirmSwitchToLiked = async () => {
    setShowConfirmDialog(false);
    setFeedback('liked');
    setEditedAnswer(answer.answer);
    if (onAnswerEdit) {
      onAnswerEdit(answer.id, answer.answer);
    }
    setFeedbackText('');
    setSavedFeedbackText('');
    setIsEditMode(false);
    setHasDislikedEdits(false);
    await updateQAF({ 
      feedback_rating: 'LIKED',
      user_edited_ans: answer.answer,
      feedback_reason: ''
    });
  };

  const handleCancelSwitchToLiked = () => {
    setShowConfirmDialog(false);
  };

  const handleThumbDown = async () => {
    const newFeedback = feedback === 'disliked' ? null : 'disliked';
    setFeedback(newFeedback);
    if (newFeedback === null) {
      setFeedbackText('');
      setSavedFeedbackText('');
      setHasDislikedEdits(false);
      if (isEditMode) {
        setIsEditMode(false);
        setEditedAnswer(answer.answer);
        if (onAnswerEdit) {
          onAnswerEdit(answer.id, answer.answer);
        }
      }
    } else {
      await updateQAF({ feedback_rating: 'DISLIKED' });
    }
  };

  const handleEditModeToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsEditMode(event.target.checked);
    if (!event.target.checked) {
      setEditedAnswer(answer.user_edited_ans || answer.answer);
      if (onAnswerEdit) {
        onAnswerEdit(answer.id, answer.user_edited_ans || answer.answer);
      }
    }
  };

  const handleAnswerChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    setEditedAnswer(newValue);
    if (onAnswerEdit) {
      onAnswerEdit(answer.id, newValue);
    }
    setTimeout(() => {
      if (textareaRef.current) {
        resizeTextarea(textareaRef.current);
      }
    }, 0);
  };

  const handleSave = async () => {
    if (editedAnswer === answer.answer) {
      setIsEditMode(false);
      return;
    }
    await updateQAF({ user_edited_ans: editedAnswer });
    if (feedback === 'disliked') {
      setHasDislikedEdits(true);
    }
  };

  const handleFeedbackSave = async () => {
    if (!feedbackText.trim()) {
      return;
    }
    
    const feedbackRating = feedback === 'liked' ? 'LIKED' : feedback === 'disliked' ? 'DISLIKED' : undefined;
    await updateQAF({
      feedback_rating: feedbackRating,
      feedback_reason: feedbackText.trim(),
    });
    setSavedFeedbackText(feedbackText.trim());
    if (feedback === 'disliked') {
      setHasDislikedEdits(true);
    }
  };

  const avgRetrievalDistance = averageRetrievalDistanceLabel(answer.retrieval_sources);
  const sortedRetrievalSources = useMemo(
    () => sortRetrievalSourcesByDistance(answer.retrieval_sources),
    [answer.retrieval_sources]
  );

  return (
    <Box sx={{ mb: 4 }}>
      <QuestionEnglishInline
        questionLocale={answer.question_locale}
        accent="theme"
        sx={{ mb: 2 }}
        originalNode={
          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.5,
              color: colors.primaryLight,
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            {(answer.question_number
              ? `${cleanQuestionNumber(answer.question_number)}. `
              : '') + cleanQuestionText(answer.question)}
          </Typography>
        }
        englishNode={
          answer.full_question_english ? (
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.5,
                color: colors.primaryLight,
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              {answer.full_question_english}
            </Typography>
          ) : null
        }
      />
      
      <Paper sx={{ 
        p: 3, 
        backgroundColor: 'white',
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        mb: 2,
        position: 'relative',
        pb: 5
      }}>
        {isEditMode ? (
          <Box sx={{ position: 'relative', mb: 2 }}>
            <TextField
              fullWidth
              multiline
              inputRef={textareaRef}
              value={editedAnswer}
              onChange={handleAnswerChange}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.95rem',
                  lineHeight: 1.5,
                  paddingRight: '80px !important',
                  alignItems: 'flex-start',
                  '& fieldset': { borderColor: '#e0e0e0' },
                  '&:hover fieldset': { borderColor: colors.primaryLight },
                  '&.Mui-focused fieldset': { borderColor: colors.primaryLight },
                },
                '& .MuiInputBase-input': {
                  paddingTop: '8px !important',
                  paddingBottom: '35px !important',
                  lineHeight: '1.5 !important',
                  overflowY: 'auto !important',
                  resize: 'none',
                  minHeight: '40px !important',
                  maxHeight: '150px !important',
                },
              }}
            />
            <Box sx={{ position: 'absolute', bottom: 8, right: 8, zIndex: 1 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  backgroundColor: colors.primaryLight,
                  '&:hover': { backgroundColor: colors.primaryHover },
                  '&:disabled': { backgroundColor: '#cccccc', color: colors.text.secondary },
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mb: 2, maxHeight: '150px', overflowY: 'auto', pr: 1 }}>
            <Typography variant="body2" sx={{ 
              lineHeight: 1.6, 
              color: colors.text.primary,
              fontSize: '0.95rem',
              whiteSpace: 'pre-line'
            }}>
              {editedAnswer}
            </Typography>
          </Box>
        )}

        <Accordion
          disableGutters
          elevation={0}
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            mb: 2,
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              minHeight: 40,
              px: 2,
              '& .MuiAccordionSummary-content': { my: 1 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <DescriptionIcon sx={{ fontSize: 18, color: colors.text.secondary }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: colors.text.primary }}>
                Retrieval sources
              </Typography>
              <Chip
                size="small"
                label={(answer.retrieval_sources?.length ?? 0).toString()}
                sx={{
                  height: 20,
                  fontSize: '0.75rem',
                  backgroundColor: '#f2f2f2',
                  color: colors.text.secondary,
                }}
              />
              {avgRetrievalDistance && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                  <Typography variant="body2" sx={{ color: colors.text.secondary, fontWeight: 500 }}>
                    Avg. vector distance: {avgRetrievalDistance}
                  </Typography>
                  <Tooltip title={AVERAGE_RETRIEVAL_DISTANCE_TOOLTIP} arrow placement="top">
                    <IconButton
                      size="small"
                      aria-label="About average vector distance"
                      onClick={(e) => e.stopPropagation()}
                      sx={{ p: 0.25, color: colors.text.secondary }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
            {sortedRetrievalSources.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="caption" sx={{ color: colors.text.secondary, lineHeight: 1.5, mb: 0.5 }}>
                  {RETRIEVAL_SCORE_LEGEND}
                </Typography>
                {sortedRetrievalSources.map((src, idx) => (
                  <Box
                    key={`${src.source_display_name}-${idx}`}
                    sx={{
                      border: '1px solid #eeeeee',
                      borderRadius: 1,
                      p: 1.5,
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, color: colors.text.primary }}>
                      {src.source_display_name || 'Source'}
                      {typeof src.relevance_score === 'number' && (
                        <Box
                          component="span"
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, ml: 0.5 }}
                        >
                          <Typography component="span" variant="body2" sx={{ color: colors.text.secondary }}>
                            · Vector distance: {formatRetrievalDistance(src.relevance_score)}
                          </Typography>
                          <Tooltip title={RETRIEVAL_DISTANCE_TOOLTIP} arrow placement="top">
                            <IconButton
                              size="small"
                              component="span"
                              aria-label="About vector distance"
                              sx={{ p: 0.25, color: colors.text.secondary }}
                            >
                              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Typography>
                    {src.text_excerpt ? (
                      <Typography
                        variant="body2"
                        sx={{ color: colors.text.secondary, mt: 0.5, whiteSpace: 'pre-wrap' }}
                      >
                        {src.text_excerpt}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ color: colors.text.disabled, mt: 0.5 }}>
                        No excerpt available.
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: colors.text.disabled }}>
                No sources retrieved for this question.
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>

        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mt: 2,
          pt: 2,
          borderTop: '1px solid #e0e0e0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              onClick={handleThumbDown}
              sx={{
                border: '1px solid #d0d0d0',
                borderRadius: '50%',
                width: 32,
                height: 32,
                color: feedback === 'disliked' ? colors.primaryLight : '#9e9e9e',
                borderColor: feedback === 'disliked' ? colors.primaryLight : '#d0d0d0',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderColor: feedback === 'disliked' ? colors.primaryLight : '#b0b0b0',
                },
              }}
            >
              <ThumbDownIcon fontSize="small" />
            </IconButton>
            
            <IconButton
              size="small"
              onClick={handleThumbUp}
              sx={{
                border: '1px solid #d0d0d0',
                borderRadius: '50%',
                width: 32,
                height: 32,
                color: feedback === 'liked' ? colors.primaryLight : '#9e9e9e',
                borderColor: feedback === 'liked' ? colors.primaryLight : '#d0d0d0',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderColor: feedback === 'liked' ? colors.primaryLight : '#b0b0b0',
                },
              }}
            >
              <ThumbUpIcon fontSize="small" />
            </IconButton>
            
            {feedback === 'disliked' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={isEditMode}
                    onChange={handleEditModeToggle}
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase': { color: '#ffffff', '&.Mui-checked': { color: '#ffffff' } },
                      '& .MuiSwitch-thumb': { backgroundColor: '#ffffff', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' },
                      '& .MuiSwitch-track': { backgroundColor: '#d0d0d0', opacity: 1 },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.primaryLight, opacity: 1 },
                      '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': { backgroundColor: '#ffffff' },
                    }}
                  />
                }
                label="Edit mode"
                sx={{
                  ml: 1,
                  '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: '#000000', fontWeight: 400 },
                }}
              />
            )}
          </Box>
          
          <CopyAnswerWithOriginalMenu
            text={editedAnswer}
            questionLocale={answer.question_locale}
            qafId={answer.qaf_id}
          />
        </Box>

        {feedback === 'disliked' && (
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Box sx={{ position: 'relative' }}>
              <TextField
                fullWidth
                multiline
                rows={1}
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Why did this answer need improvement?"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.875rem',
                    minHeight: '40px !important',
                    paddingRight: '80px !important',
                    '& fieldset': { borderColor: '#d0d0d0' },
                    '&:hover fieldset': { borderColor: colors.primaryLight },
                    '&.Mui-focused fieldset': { borderColor: colors.primaryLight },
                  },
                  '& .MuiInputBase-input': {
                    paddingTop: '8px !important',
                    paddingBottom: '8px !important',
                    minHeight: '24px !important',
                    maxHeight: '24px !important',
                    lineHeight: '1.5 !important',
                  },
                }}
              />
              {feedbackText.trim() !== savedFeedbackText.trim() && (
                <Box sx={{ position: 'absolute', bottom: 4, right: 4, zIndex: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleFeedbackSave}
                    disabled={!feedbackText.trim() || saving}
                    size="small"
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      padding: '4px 12px',
                      minWidth: 'auto',
                      backgroundColor: colors.primaryLight,
                      '&:hover': { backgroundColor: colors.primaryHover },
                      '&.Mui-disabled': { backgroundColor: '#d0d0d0', color: '#9e9e9e' },
                    }}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>{error}</Alert>
        </Snackbar>

        <Snackbar open={!!successMessage} autoHideDuration={3000} onClose={() => setSuccessMessage(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>{successMessage}</Alert>
        </Snackbar>

        <Dialog open={showConfirmDialog} onClose={handleCancelSwitchToLiked} aria-labelledby="confirm-dialog-title" aria-describedby="confirm-dialog-description">
          <DialogTitle id="confirm-dialog-title">Confirm Switch to Liked</DialogTitle>
          <DialogContent>
            <DialogContentText id="confirm-dialog-description">
              Earlier it was marked as disliked. Now moving to liked will revert your edited answer and feedback reason.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelSwitchToLiked} color="inherit">Cancel</Button>
            <Button 
              onClick={handleConfirmSwitchToLiked} 
              variant="contained"
              sx={{ backgroundColor: colors.primaryLight, '&:hover': { backgroundColor: colors.primaryHover } }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
};

export default AnswerCard;

